"use client";

import { useId } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ChannelsSection } from "@/components/doc/left-sidebar/chats-tab/ChannelsSection";
import { DirectMessagesSection } from "@/components/doc/left-sidebar/direct-messages/DirectMessagesSection";
import { NavInstructions } from "@/components/doc/left-sidebar/NavInstructions";
import { TeamMembers } from "@/components/doc/left-sidebar/team-members/TeamMembers";
import { TOP_MENU_BUTTON_OPEN_CLASS_NAME } from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { UnreadCountBadge } from "@/components/doc/left-sidebar/top-menu/UnreadCountBadge";
import { useSidebarUnreadCounts } from "@/components/doc/left-sidebar/top-menu/useSidebarUnreadCounts";
import { NavTree } from "@/components/doc/left-sidebar/useSidebarNav";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { useTeamChats } from "@/store/useTeamChats";
import { twMerge } from "@/utils/twMerge";

export const TeamSectionsPopover = observer(function TeamSectionsPopover() {
  const teamChats = useTeamChats();
  const { classroom, teams } = useStore();
  const { channelsUnread } = useSidebarUnreadCounts();
  const t = useTranslations("documentsSidebar");
  const instructionsId = useId();

  // Students get no chats or member list (docs/plans/student-permissions.md).
  if (classroom.isStudentIn(teams.currentTeam)) return null;

  const panelTitle = teamChats.isAvailable ? t("teamSectionsPanel") : t("chats");

  return (
    <Popover className="relative flex w-full min-h-0">
      {({ open }) => (
        <>
          <PopoverButton
            as={Sidebar.Button}
            icon={
              <span className="relative flex">
                <Icon name="chat/ChatLarge" />
                <UnreadCountBadge
                  count={channelsUnread}
                  label={`${channelsUnread} unread chat messages`}
                />
              </span>
            }
            title={panelTitle}
            className={twMerge(open && TOP_MENU_BUTTON_OPEN_CLASS_NAME)}
          />
          <PopoverPanel
            portal
            anchor={{ to: "right start", gap: 14, padding: 10 }}
            className={twMerge(
              "z-100 flex min-h-0 flex-col rounded-12 p-1.25",
              "bg-block-bg shadow-md dark:shadow-block-inverted",
              "outline-none",
              "w-sidebar-width min-w-sidebar-width max-h-[min(70vh,calc(100vh-3rem))]",
            )}
          >
            {/* The nav scrolls, not the panel: ChannelsSection fits its rows to its parent's height */}
            <nav
              aria-label={panelTitle}
              className="flex min-h-0 flex-col overflow-y-auto"
              aria-describedby={instructionsId}
            >
              <NavInstructions id={instructionsId} />
              <NavTree aria-label={panelTitle}>
                {teamChats.isAvailable && <ChannelsSection />}
                <DirectMessagesSection />
                {teamChats.isAvailable && <TeamMembers />}
              </NavTree>
            </nav>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
});
